import { autoinject, computedFrom, observable } from 'aurelia-framework';
import { RouteConfig, Router } from 'aurelia-router';
import { BaseVettingEntity } from "./bases/base-vetting-entity";
import { DueDiligenceClientModel, VetStatusEnum, VettingStepEnum } from "../../../models/due-diligence.models";
import { EntityTypes, TabModel } from "@rightship-tech/common-ui/models/basic.models";
import { Claims } from "@rightship-tech/common-ui/constants/authentication/claims";
import { DueDiligenceOverview } from "./tabs/overview/due-diligence-overview";
import { UserClaimsService } from "@rightship-tech/common-ui/services/basic/user-claims.service";
import { NotificationClientService } from "@rightship-tech/common-ui/services/basic/notification-client.service";
import { DueDiligenceDocuments } from "./tabs/documents/due-diligence-documents";
import { DueDiligenceApi } from "../../../apis/due-diligence.api";
import { DueDiligenceRulesRun } from "./tabs/rules-run/due-diligence-rules-run";
import { UserClientService } from "@rightship-tech/common-ui/services/basic/user-client.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { VesselApi } from "../../../apis/vessel.api";
import { LookupClientService } from "@rightship-tech/common-ui/services/lookups/lookup-client.service";
import { SignalFunctions } from "../../../constants/signal.constants";
import { DueDiligenceHistoryTrail } from "./tabs/history-trail/due-diligence-history-trail";
import { DueDiligenceClaims } from "../../../constants/claims.constants";
import { DueDiligenceMessageCenter } from "./tabs/message-center/due-diligence-message-center";
import moment from "moment";
import { LookupValueRequest } from "@rightship-tech/common-ui/models/lookup.models";
import { LookupValueQuery } from "@rightship-tech/common-ui/constants/lookup.constants";
import { GetVesselResponse } from "../../../models/vessel.models";
import { SafetyRatingApi } from "../../../apis/safety-rating.api";
import { DueDiligenceEmailHistory } from "./tabs/email-history/due-diligence-email-history";
import { PartialTabbedEntityItem } from "@rightship-tech/common-ui/partials/partial-tabbed-entity-item/partial-tabbed-entity-item";
import { DueDiligenceNotes } from './tabs/notes/due-diligence-notes';

@autoinject
export class DueDiligence extends BaseVettingEntity {

	public canViewDueDiligence: boolean = true;
	public showTransactionNumber: boolean;
	public showCharterType: boolean;
	public showShipToShip: boolean;
	public showIncoTerms: boolean;
	public dueDiligenceHeaderElem: HTMLElement;
	public showVesselMoreDetailsDrawer: boolean;
	public skipForcedShipManagerEmailInput: boolean;
	public isRightShipUser: boolean;
	public vesselUrl: string;
    public showBlackSeaTrade: boolean = false;
	public vetForBST: boolean;
	public selectedTab: TabModel;
	public vesselCargoType: string;
	public vessel: GetVesselResponse;
    public currentTab: TabModel = null;
    public initialStep: string;
	private messageCenterSubtaskId: number;

    @observable()
    public entityItem: PartialTabbedEntityItem;

	@computedFrom("item", "item.vetStatusId")
	get isClosed() {
		return this.item && (this.item.vetStatusId == VetStatusEnum.DD_Acceptable
			|| this.item.vetStatusId == VetStatusEnum.DD_Unacceptable);
	}

	@computedFrom("selectedTab")
	get isOverviewTabSelected() {
		if (this.selectedTab) {
			return this.selectedTab.step == VettingStepEnum.VetOverview;
		}
	}

	//Arrival date
	@computedFrom("item", "item.arrivalDate")
	public get arrivalDate(): string {
		if (this.item && this.item.arrivalDate) {
			return moment(this.item.arrivalDate).format("DD MMM YYYY");
		}
	}

	//Departure date
	@computedFrom("item", "item.arrivalDate")
	public get departureDate(): string {
		if (this.item && this.item.departureDate) {
			return moment(this.item.departureDate).format("DD MMM YYYY");
		}
	}

    getShow(s: TabModel) {
        return "show" in s ? (typeof (s.show) == "function" ? s.show() : s.show) : true;
    }
	
    setCurrentStep(routeStep: string) {
        let step: TabModel = null;
        const steps = this.tabs.filter(s => this.getShow(s));
        if (routeStep != undefined) {
            step = steps.filter(s => s.param == routeStep)[0];
        }

        if (!step) {
            step = steps[0];
        }

        if (this.entityItem) {

            //If is freemium, filter the list of steps and get the first item which is not freemium
            if (step.lockedFeature) {
                step = steps.filter(x => !x.lockedFeature)[0];
            }

            this.entityItem.currentStep = step;
        }
    }

	constructor(private router: Router, userClaimsService: UserClaimsService, protected notificationService: NotificationClientService,
		protected dueDilignceOverview: DueDiligenceOverview, protected vetRulesRun: DueDiligenceRulesRun, protected vetDocuments: DueDiligenceDocuments,
		protected dueDiligenceMessageCenter: DueDiligenceMessageCenter,
		protected signalClientService: SignalClientService, protected userClientService: UserClientService, private vettingService: DueDiligenceApi,
		vesselApi: VesselApi, private dueDiligenceHistoryTrail: DueDiligenceHistoryTrail, private lookupClientService: LookupClientService, 
		safetyRatingApi: SafetyRatingApi, private dueDiligencEmailHistory: DueDiligenceEmailHistory, private dueDiligenceNotes: DueDiligenceNotes) {
		super(userClaimsService, notificationService, vetDocuments, signalClientService, userClientService, vesselApi, lookupClientService, safetyRatingApi);

		this.vesselUrl = '/vessel';
		this.dueDilignceOverview.reloadItemFunc = this.reloadItemFunc.bind(this);
		this.skipForcedShipManagerEmailInput = this.userClaimsService.hasPermission(Claims.SkipForcedShipManagerEmailInput);
		this.isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        this.showBlackSeaTrade = userClaimsService.hasPermission(DueDiligenceClaims.ShowBlackSeaTrade);
        this.showTransactionNumber = userClaimsService.hasPermission(Claims.ShowTransactionNumber);
        this.showCharterType = userClaimsService.hasPermission(Claims.ShowCharterType);
        this.showShipToShip = userClaimsService.hasPermission(Claims.ShowShipToShip);
        this.showIncoTerms = userClaimsService.hasPermission(Claims.ShowIncoTerms);
	}

	public async activate(params: any, routeConfig: RouteConfig): Promise<void> {
		await super.activate(params, routeConfig);

		this.tabs = this.getTabs();
		let currentTab = null;
		if (params && params.step) {
			this.initialStep = params.step;

			if (params.step === 'rfi-correspondence') {
				currentTab = this.tabs.filter(x => x.step == VettingStepEnum.VetMessageCenter)[0];
			}
		}

		if (currentTab) {
			this.currentTab = currentTab;
			if(this.currentTab.onClick)
				this.currentTab.onClick(null);
		}
	}

	async attached() {
		super.attached();
		
        if (this.initialStep)
            this.setCurrentStep(this.initialStep);

		//Get vessel cargo type
		this.getVesselCargoType(this.item.cargoTypeId);

		//Subscribe signalR
		this.signalClientService.subscribe(SignalFunctions.vetTaskUpdateChanged, this.onVetTaskUpdated);
		this.signalClientService.subscribe(SignalFunctions.refreshDueDiligence, this.refreshVet);
		this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Vet), this.refreshVetStatus);
		
        //Get vessel
        this.vessel = await this.vesselApi.getVessel({ vesselId: this.item.vesselId, imo: null, bKey: null });
		await this.dueDilignceOverview.setVessel(this.vessel, this.item);

		//Doing setTimeout so that the tabs are re-loaded after the view is rendered and message center count is computed correctly.
		setTimeout(() => {
			this.tabs = this.getTabs();
		});
	}

	detached() {
		super.detached();
		this.signalClientService.unsubscribe(SignalFunctions.vetTaskUpdateChanged, this.onVetTaskUpdated);
		this.signalClientService.unsubscribe(SignalFunctions.refreshDueDiligence, this.refreshVet);
		this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.Vet), this.refreshVetStatus);
	}

	dateFormat(d:Date){
		return d? moment(d).format("DD MMM YYYY"): null;
	}

	protected getTabs(): TabModel[] {
		return [
			{
				lazyLoad: true,
				title: "Overview",
				step: VettingStepEnum.VetOverview,
				vm: this.dueDilignceOverview,
				getModel: () => { 
					return {
						dueDiligenceHeaderElem: this.dueDiligenceHeaderElem,
						item: this.item
					}
				 },
				show: true
			},
			{
				lazyLoad: true,
				title: () => this.getTitle("Message Centre", this.dueDiligenceMessageCenter?.messageCentre?.getAllUnreadMessagesCount),
				step: VettingStepEnum.VetMessageCenter,
				vm: this.dueDiligenceMessageCenter,
				param: "rfi-correspondence",
				getModel: () => {
					const subtaskId = this.messageCenterSubtaskId? Number(this.messageCenterSubtaskId): null;
					this.messageCenterSubtaskId = null;
					return {
						item: this.item,
						email: this.userClientService.currentUser.user.email,
						subtaskId: subtaskId
					};
				},
				show: this.userClaimsService.hasPermission(DueDiligenceClaims.CanViewDueDiligenceMessageCenterTab)
			},
			{
				lazyLoad: true,
				title: () => this.getTitle("Documents", this.item?.documentsCount),
				step: VettingStepEnum.VetDocuments,
				vm: this.vetDocuments,
				getModel: () => { return { vesselId: this.item?.vesselId }},
				show: this.userClaimsService.hasPermission(DueDiligenceClaims.CanViewDueDiligenceDocumentsTab)
			},
			{
				lazyLoad: true,
				title: "Rules Run",
				step: VettingStepEnum.VetRulesRun,
				vm: this.vetRulesRun,
				show: this.userClaimsService.hasPermission(DueDiligenceClaims.CanViewDueDiligenceRunsTab),
				getModel: () => { 
					return {
						dueDiligenceRuleRun: this.item,
					}
				}
			},
			{
				lazyLoad: true,
				title: "History Trail",
				step: VettingStepEnum.HistoryTrail,
				vm: this.dueDiligenceHistoryTrail,
				show: this.userClaimsService.hasPermission(DueDiligenceClaims.CanViewDueDiligenceHistoryTrail),
				getModel: () => { 
					return {
						dueDiligenceHistoryTrail: this.item,
					}
				}
			},
			{
				lazyLoad: true,
				title: "Email History",
				step: VettingStepEnum.EmailHistory,
				vm: this.dueDiligencEmailHistory,
				show: () => this.userClaimsService.hasPermission(DueDiligenceClaims.CanViewDueDiligenceEmailHistory),
				getModel: () => { 
					return {
						item: this.item,
					}
				}
			},
			{
				lazyLoad: true,
				title: "Notes",
				step: VettingStepEnum.Notes,
				vm: this.dueDiligenceNotes,
				show: () => this.userClaimsService.hasPermission(DueDiligenceClaims.CanAccessNotesInDueDiligence),
				getModel: () => { 
					return {
						entityTypeId: EntityTypes.Vet,
						entityId: this.item.vetId,
						item: this.item
					}
				}
			}
		]
	}

	public getTitle(title: string, count: number) {
		if (count == null) return title;
		return title + " (" + count + ")";
	}

	protected async getItem(id: number): Promise<DueDiligenceClientModel> {
		let response;
		try {
			response = await this.vettingService.getDueDiligence(id, true);
		} 
		catch (e) {
			if (e.statusCode === 401) {
				this.canViewDueDiligence = false;
			}
		}
		return response;
	}

	protected getItemId(): number {
		return this.item ? this.item.vetId : null;
	}

	protected onVetTaskUpdated = (params: { vetId: number }) => {
		if (this.item.vetId != params.vetId) return;

		this.vettingService.getDueDiligence(this.item.vetId, true).then(x => {
			this.item = x;
		});
	}

	toggleVesselMoreDetailsDrawer() {
		this.showVesselMoreDetailsDrawer = !this.showVesselMoreDetailsDrawer;
	}

	//Navigate back
	public navigateBack() {
		this.router.navigateBack();
	}

	//Can navigate back
	public canNavigateBack(): boolean {
		const urlParams = new URLSearchParams(window.location.search);
		const origin = urlParams.get('origin');

		//If the user is coming from a request, they should not be able to navigate back as it will get them back to the request page
		return this.router.history && this.router.history.getHistoryIndex() > 0 && origin != "request";
	}

	//On tab clicked
    public onTabClicked = (tab: TabModel) => {
        if (tab) {
			this.selectedTab = tab;
        }
    }

	//Vessel cargo type
	public getVesselCargoType(cargoTypeId: number): void {
		if (cargoTypeId) {

			//Get cargo types lookup
			const request = new LookupValueRequest();
			request.lookupValueQuery = LookupValueQuery.VesselCargoTypes;
			this.lookupClientService.getLookup(request).then(x => {
				this.vesselCargoType = x.activeValues.filter(x => x.id == cargoTypeId)[0].name;
			});
		}
	}

    entityItemChanged() {
        if (this.entityItem && this.tabs && this.tabs.length > 0) {
            this.stepsInitialized();
        }
    }

    stepsInitialized() {
        if (this.entityItem) {
            if (this._params && this._params.subtaskId && this._params.subtaskId > 0) {
                this.viewMessageCenterBySubtaskId(this._params.subtaskId);
                window.history.replaceState(window.history.state, 'Due Diligence', window.location.href.replace(`?subtaskId=${this._params.subtaskId}`, ''));
            }
        }
    }

    viewMessageCenterBySubtaskId(subtaskId: number) {
        this.messageCenterSubtaskId = subtaskId;
        const step = this.tabs.filter(x => x.step == VettingStepEnum.VetMessageCenter)[0];
        this.entityItem.currentStep = step;
    }
}